import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  Wallet,
  ShieldCheck,
  Timer,
  Gift,
  ArrowRight,
  Lock,
  Users,
  TrendingUp,
  CheckCircle2,
  ArrowLeftRight,
  Sparkles,
  Coins,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { priceAPI } from '@/lib/api';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { LanguageToggle } from '@/components/LanguageToggle';

const featureIcons = [Gift, Users, Lock, ShieldCheck, ArrowLeftRight, TrendingUp];
const featureKeys = ['f1', 'f2', 'f3', 'f4', 'f5', 'f6'];

export default function Landing() {
  const [prices, setPrices] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await priceAPI.getPrices();
        setPrices(res.data.prices);
      } catch (err) {
        console.error('Price fetch error:', err);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 60000);
    return () => clearInterval(interval);
  }, []);

  const tickerData = prices ? [
    { name: 'BTC', price: prices.bitcoin?.usd, change: prices.bitcoin?.usd_24h_change },
    { name: 'ETH', price: prices.ethereum?.usd, change: prices.ethereum?.usd_24h_change },
    { name: 'TRX', price: prices.tron?.usd, change: prices.tron?.usd_24h_change },
    { name: 'BNB', price: prices.binancecoin?.usd, change: prices.binancecoin?.usd_24h_change },
    { name: 'USDT', price: prices.tether?.usd, change: prices.tether?.usd_24h_change },
  ] : [];

  const faqKeys = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
  const stepKeys = ['s1', 's2', 's3', 's4'];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Ticker */}
      {tickerData.length > 0 && (
        <div data-testid="home-live-ticker" className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden">
          <div className="ticker-scroll flex items-center gap-8 py-2 px-4 whitespace-nowrap" style={{ width: 'max-content' }}>
            {[...tickerData, ...tickerData].map((item, i) => (
              <span key={i} className="flex items-center gap-2 text-sm" data-testid="ticker-item">
                <span className="font-semibold text-[hsl(var(--foreground))]">{item.name}</span>
                <span className="tabular-nums">${item.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
                <span className={item.change >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}>
                  {item.change >= 0 ? '+' : ''}{item.change?.toFixed(2)}%
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--card))]/60 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary))] flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[hsl(var(--primary-foreground))]" />
            </div>
            <span className="font-bold text-xl font-['Space_Grotesk'] tracking-tight">Safe Crypto</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageToggle variant="nav" />
            <Button variant="ghost" asChild className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]">
              <Link to="/login" data-testid="nav-login-button">{t('common.login')}</Link>
            </Button>
            <Button asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90">
              <Link to="/register" data-testid="home-hero-register-button">{t('common.register')}</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(700px circle at 15% 10%, rgba(18,200,178,0.16), transparent 55%), radial-gradient(900px circle at 85% 20%, rgba(23,185,230,0.14), transparent 60%)' }} />
        <div className="noise-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/20 hover:bg-[hsl(var(--primary))]/15">
                  <Gift className="w-3 h-3 mr-1" /> {t('landing.heroSubtitle')}
                </Badge>
                <Badge className="bg-[rgba(247,200,75,0.12)] text-[#F7C84B] border-[#F7C84B]/20 hover:bg-[rgba(247,200,75,0.18)]">
                  <Users className="w-3 h-3 mr-1" /> {t('landing.heroBadge2')}
                </Badge>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight font-['Space_Grotesk'] leading-tight">
                {t('landing.heroTitle1')}
                <span className="block text-[hsl(var(--primary))]">{t('landing.heroTitle2')}</span>
              </h1>
              <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] max-w-lg">
                {t('landing.heroDesc')} <strong className="text-[hsl(var(--foreground))]">{t('landing.heroBonus')}</strong> {t('landing.heroBonusEnd')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button size="lg" asChild className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 text-base px-8 h-12 active:scale-[0.98] transition-colors duration-200">
                  <Link to="/register">{t('landing.heroCta')} <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
                <Button size="lg" variant="outline" className="border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-white/5 h-12" data-testid="home-hero-how-it-works-button">
                  <a href="#nasil-calisir">{t('landing.heroSecondary')}</a>
                </Button>
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]/70">
                {t('landing.heroDisclaimer')}
              </p>
            </div>

            {/* Right side cards */}
            <div className="space-y-4 lg:pl-8">
              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Sparkles className="w-5 h-5 text-[#F7C84B]" />
                    <span className="font-semibold">{t('landing.bonusCalcTitle')}</span>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[hsl(var(--muted-foreground))]">{t('landing.bonusWelcome')}</span>
                      <span className="font-semibold text-[#2DD36F] tabular-nums">+$200 USDT</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[hsl(var(--muted-foreground))]">{t('landing.bonusReferral')}</span>
                      <span className="font-semibold text-[#2DD36F] tabular-nums">+$25 USDT</span>
                    </div>
                    <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                      <span className="font-medium">{t('landing.bonusTotal')}</span>
                      <span className="text-xl font-bold text-[hsl(var(--primary))] tabular-nums">$225 USDT</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Lock className="w-5 h-5 text-[hsl(var(--primary))]" />
                    <span className="font-semibold">{t('landing.rateLockActive')}</span>
                    <Badge className="ml-auto bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]">24h</Badge>
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">
                    {t('landing.rateLockDesc')}
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-white/5 backdrop-blur-md border-white/10">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Coins className="w-5 h-5 text-[hsl(var(--accent))]" />
                    <span className="font-semibold">{t('landing.easyWithdraw')}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" />
                      <span>{t('landing.step1')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" />
                      <span>{t('landing.step2')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-[hsl(var(--primary))]" />
                      <span>{t('landing.step3')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Banner */}
      <section className="py-12 border-t border-[hsl(var(--border))] bg-gradient-to-r from-[hsl(var(--card))] via-[rgba(18,200,178,0.05)] to-[hsl(var(--card))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[rgba(247,200,75,0.12)] flex items-center justify-center">
                <Users className="w-7 h-7 text-[#F7C84B]" />
              </div>
              <div>
                <h3 className="text-xl font-semibold font-['Space_Grotesk']">{t('landing.referralBanner')}</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('landing.referralBannerDesc')}</p>
              </div>
            </div>
            <Button size="lg" asChild className="bg-[#F7C84B] text-[hsl(var(--background))] hover:bg-[#F7C84B]/90 font-semibold px-8 active:scale-[0.98] transition-colors duration-200">
              <Link to="/register">{t('landing.referralCta')}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 border-t border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold font-['Space_Grotesk'] tracking-tight">{t('landing.whyTitle')}</h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
              {t('landing.whyDesc')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featureKeys.map((key, i) => {
              const Icon = featureIcons[i];
              return (
                <Card key={key} className="bg-[hsl(var(--card))] border-[hsl(var(--border))] hover:bg-white/[0.02] transition-colors duration-200">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-semibold text-lg mb-2">{t(`landing.features.${key}title`)}</h3>
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">{t(`landing.features.${key}desc`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="nasil-calisir" className="py-20 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-semibold font-['Space_Grotesk'] tracking-tight">{t('landing.howTitle')}</h2>
            <p className="mt-4 text-[hsl(var(--muted-foreground))]">{t('landing.howDesc')}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stepKeys.map((key, i) => (
              <div key={key} className="relative">
                <div className="text-5xl font-bold text-[hsl(var(--primary))]/10 font-['Space_Grotesk'] mb-3">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-semibold text-lg mb-2">{t(`landing.steps.${key}title`)}</h3>
                <p className="text-sm text-[hsl(var(--muted-foreground))]">{t(`landing.steps.${key}desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 border-t border-[hsl(var(--border))]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-semibold font-['Space_Grotesk'] tracking-tight">{t('landing.faqTitle')}</h2>
          </div>
          <Accordion type="single" collapsible data-testid="home-faq-accordion">
            {faqKeys.map((key, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-[hsl(var(--border))]">
                <AccordionTrigger className="text-left hover:no-underline hover:text-[hsl(var(--primary))]">{t(`landing.faqs.${key}`)}</AccordionTrigger>
                <AccordionContent className="text-[hsl(var(--muted-foreground))]">{t(`landing.faqs.a${key.slice(1)}`)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[hsl(var(--border))] bg-[hsl(var(--card))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[hsl(var(--primary))]" />
            <span className="font-semibold font-['Space_Grotesk']">Safe Crypto</span>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            &copy; 2024-2026 Safe Crypto. {t('landing.footer')}
          </p>
        </div>
      </footer>
    </div>
  );
}
